import RBACFlag from "@/app/components/core/RBACFlag";
import PetName from "@/app/components/ui/Reusable/PetName";
import Species from "@/app/components/ui/Reusable/Species";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useBookingSummaryAPI } from "@/app/hooks/api_hooks/bookings/useBookingSummaryAPI";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useState } from "react";
import PWLActionModal, { IPWLActionModalState } from "@components/routes/modals/PWLAction";
import { useLoader } from "@/app/hooks/useLoader";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import Username from "@/app/components/ui/Reusable/Username";
import type { IBookingSummaryResponseData } from "@/api_framework/api_modals/Bookings";

const AppointmentClosingComponent = () => {
  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();
  const { appointmentButtonMapping } = useMappedConstants();
  const { veticEpocToTime, getDisplayStaffName, statusChipPWL, convertToReadableFormat } = useUtils();
  const { NO_APPOINTMENTS } = useImageConstants();

  const [open, setOpen] = useState(false);
  const [examption, setExamption] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [actionModalData, setActionModalData] = useState<IPWLActionModalState | null>(null);
  const [bookingSummaryData, setBookingSummaryData] = useState<IBookingSummaryResponseData | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const { execute: fetchBookingSummary, isBookingSummaryInProgress } = useBookingSummaryAPI();

  const isLoading = useLoader("isClosingDataInProgress");

  // ✅ read query params
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const time = queryParams.get("t") ?? "";

  useEffect(() => {
    if (queryClinics && queryClinics) {
      fetchDayClosingData(queryClinics, queryShift, "appointments", "status");
    }
  }, [processId, queryCity, queryClinics, queryShift, time]);
  const clinicsWithAppointments = closureData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  const handleAction = (data: any) => {
    const bookingId = data?.booking_id;
    setActionModalData(data);
    setBookingSummaryData(null);
    setOpen(true);
    if (bookingId) {
      fetchBookingSummary(bookingId).then((summaryData) => {
        setBookingSummaryData(summaryData);
      });
    }
  };
  const formatStatusLabel = (status?: string) => (status ? convertToReadableFormat(status) : "-");

  const fetchAppointmentData = () => {
    fetchDayClosingData(queryClinics, queryShift, "appointments", "status");
  };

  return (
    <>
      <div className=" w-full pb-4">
        {isLoading ? (
          <div>
            <TableSkeletonComponent columnLength={8} />
          </div>
        ) : clinicsWithAppointments.length > 0 ? (
          <div className="space-y-3">
            {clinicsWithAppointments.map((clinic) => (
              <div
                key={clinic.clinic_id}
                className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm"
              >
                    <div className="overflow-x-auto rounded-xl">
                      <table className="w-full min-w-[1180px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                          <tr>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Client Name</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Pet Details</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Appointment Status</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Appointment Type</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Service Provider</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Time</th>
                            <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Actions</th>
                          </tr>
                        </thead>

                        <tbody className="text-xs">
                          {clinic.closure_list.map((item, rowIndex) => {
                            const isFirst = rowIndex === 0;
                            const isLast = rowIndex === clinic.closure_list.length - 1;
                            const isSingle = clinic.closure_list.length === 1;
                            return (
                            <tr
                              key={item.booking_id}
                              className={`
                                bg-white hover:bg-[#FCFCFD] transition-colors
                                ${isSingle ? "rounded-xl" : ""}
                                ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                                ${isLast && !isSingle ? "rounded-b-xl" : ""}
                              `}
                            >
                              <td
                                className={`
                                  p-3 text-xs text-blue-600
                                  ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                                `}
                              >
                                <Username
                                  username={item.user_details?.name}
                                  petId={item.pet_details?.id}
                                  userId={item.user_details?.id}
                                />
                              </td>

                              <td className="p-3 text-xs">
                                <div className="flex flex-col gap-1">
                                  <PetName petName={item.pet_details?.name} className="text-xs" petStatus="" />
                                  <Species
                                    speciesId={item.pet_details?.pet_species_type_id}
                                    className="text-[#6C727F] text-xs"
                                  />
                                </div>
                              </td>
                              <td className="p-3 text-xs">
                                {item?.booking_status ? (
                                  <span className={statusChipPWL(formatStatusLabel(item?.booking_status))}>
                                    {formatStatusLabel(item?.booking_status)}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td className="p-3 text-[10px] sm:text-xs text-black ">
                                <p>{item.service_details?.service_type}</p>
                              </td>

                              <td className="p-3 text-xs">{getDisplayStaffName(item) || "NA"}</td>
                              <td className="p-3 text-xs text-[#6C727F] whitespace-nowrap">
                                {veticEpocToTime(item?.booking_start_date_time)}
                              </td>

                              <td
                                className={`
                                  p-3 w-[170px] text-[10px] sm:text-xs 2xl:text-xs font-normal
                                  ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                  ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                  ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                                `}
                              >
                                {!item?.closure_status && (
                                  <div className="flex gap-2 justify-start">
                                    {appointmentButtonMapping[item?.booking_status?.toLowerCase()] ? (
                                      <RBACFlag actionId="ACTION_BUTTON_PWL">
                                        <button
                                          className="bg-black px-4 py-2 rounded-lg text-[10px] sm:text-xs text-white font-medium whitespace-nowrap hover:opacity-90 transition-opacity"
                                          onClick={() => handleAction(item)}
                                        >
                                          {appointmentButtonMapping[item?.booking_status?.toLowerCase()]}
                                        </button>
                                      </RBACFlag>
                                    ) : (
                                      <p className="w-20"></p>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
            ))}
          </div>
        ) : (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Appointment Found" />
        )}
      </div>

      {open && (
        <PWLActionModal
          open={open}
          setOpen={setOpen}
          actionModalData={actionModalData}
          source={"day_closing_module"}
          bookingSummaryData={bookingSummaryData}
          isBookingSummaryInProgress={isBookingSummaryInProgress}
        />
      )}
  
    </>
  );
};
export default AppointmentClosingComponent;
